const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function for fetch requests
async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Not found');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface CourseEnrollment {
  id: number;
  userId: number;
  courseId: number;
  status: 'enrolled' | 'in_progress' | 'completed' | 'failed' | 'suspended';
  progressPercentage: string;
  score?: string;
  timeSpentSeconds: number;
  currentLessonId?: number;
  completedLessons: number[];
  enrolledAt: string;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
}

export interface LessonProgress {
  id: number;
  userId: number;
  lessonId: number;
  enrollmentId?: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'passed' | 'failed';
  progressPercentage: string;
  score?: string;
  timeSpentSeconds: number;
  attempts: number;
  suspendData?: any;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt?: string;
}

export interface XApiStatement {
  id?: string;
  actor: {
    objectType: 'Agent';
    name?: string;
    mbox?: string;
    account?: {
      homePage: string;
      name: string;
    };
  };
  verb: {
    id: string;
    display: { [lang: string]: string };
  };
  object: {
    id: string;
    objectType: string;
    definition?: {
      name?: { [lang: string]: string };
      description?: { [lang: string]: string };
      type?: string;
    };
  };
  result?: {
    score?: {
      scaled?: number;
      raw?: number;
      min?: number;
      max?: number;
    };
    success?: boolean;
    completion?: boolean;
    duration?: string;
    response?: string;
    extensions?: { [key: string]: any };
  };
  context?: {
    contextActivities?: {
      parent?: any[];
      grouping?: any[];
      category?: any[];
      other?: any[];
    };
    registration?: string;
    instructor?: any;
    team?: any;
    revision?: string;
    platform?: string;
    language?: string;
    extensions?: { [key: string]: any };
  };
  timestamp?: string;
}

export interface ResumePoint {
  enrollmentId: number;
  currentLessonId?: number;
  completedLessons: number[];
  progressPercentage: string;
  nextLesson?: {
    id: number;
    title: string;
    order: number;
  };
}

export interface Statistics {
  totalEnrollments: number;
  completedCount: number;
  inProgressCount: number;
  avgProgress: number;
  avgScore: number;
  totalTimeSpent: number;
  recentActivity?: any[];
  courseStats?: { [courseId: number]: any };
}

class TrackingService {
  private currentUserId: number | null = null;

  setUserId(userId: number) {
    this.currentUserId = userId;
  }

  /**
   * Enroll user in a course
   */
  async enrollInCourse(courseId: number, userId?: number): Promise<CourseEnrollment> {
    const uid = userId || this.currentUserId;
    return fetchAPI(`${API_BASE_URL}/api/progress/enroll`, {
      method: 'POST',
      body: JSON.stringify({ courseId, userId: uid }),
    });
  }

  /**
   * Get enrollment for a course
   */
  async getEnrollment(courseId: number): Promise<CourseEnrollment | null> {
    try {
      const userId = this.currentUserId || 1;
      return await fetchAPI(`${API_BASE_URL}/api/progress/enrollment/${courseId}?userId=${userId}`);
    } catch (error: any) {
      if (error.message === 'Not found') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update lesson progress
   */
  async updateLessonProgress(
    lessonId: number,
    data: {
      status?: LessonProgress['status'];
      progressPercentage?: number;
      score?: string;
      timeSpentSeconds?: number;
      suspendData?: any;
    }
  ): Promise<LessonProgress> {
    const userId = this.currentUserId || 1;
    return fetchAPI(`${API_BASE_URL}/api/progress/lesson/${lessonId}`, {
      method: 'POST',
      body: JSON.stringify({ ...data, userId }),
    });
  }

  /**
   * Get lesson progress
   */
  async getLessonProgress(lessonId: number): Promise<LessonProgress | null> {
    try {
      const userId = this.currentUserId || 1;
      return await fetchAPI(`${API_BASE_URL}/api/progress/lesson/${lessonId}?userId=${userId}`);
    } catch (error: any) {
      if (error.message === 'Not found') {
        return null;
      }
      throw error;
    }
  }

  /**
   * Mark lesson as complete
   */
  async completeLesson(lessonId: number): Promise<LessonProgress> {
    const userId = this.currentUserId || 1;
    return fetchAPI(`${API_BASE_URL}/api/progress/lesson/${lessonId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  /**
   * Get resume point for a course
   */
  async getResumePoint(courseId: number): Promise<ResumePoint> {
    const userId = this.currentUserId || 1;
    return fetchAPI(`${API_BASE_URL}/api/progress/resume/${courseId}?userId=${userId}`);
  }

  /**
   * Send xAPI statement
   */
  async sendXApiStatement(statement: XApiStatement): Promise<{ id: string }> {
    // Generate statement ID if not provided
    if (!statement.id) {
      statement.id = this.generateUUID();
    }

    // Add timestamp if not provided
    if (!statement.timestamp) {
      statement.timestamp = new Date().toISOString();
    }

    return fetchAPI(`${API_BASE_URL}/api/xapi/statements`, {
      method: 'POST',
      body: JSON.stringify(statement),
    });
  }

  /**
   * Send multiple xAPI statements
   */
  async sendXApiStatements(statements: XApiStatement[]): Promise<{ ids: string[] }> {
    // Add IDs and timestamps to statements that don't have them
    const processedStatements = statements.map((stmt) => ({
      ...stmt,
      id: stmt.id || this.generateUUID(),
      timestamp: stmt.timestamp || new Date().toISOString(),
    }));

    return fetchAPI(`${API_BASE_URL}/api/xapi/statements`, {
      method: 'POST',
      body: JSON.stringify(processedStatements),
    });
  }

  /**
   * Query xAPI statements
   */
  async queryXApiStatements(params: {
    actor?: string;
    verb?: string;
    activity?: string;
    since?: string;
    until?: string;
    limit?: number;
  }): Promise<{ statements: XApiStatement[] }> {
    const queryString = new URLSearchParams(params as any).toString();
    return fetchAPI(`${API_BASE_URL}/api/xapi/statements?${queryString}`);
  }

  /**
   * Get user statistics
   */
  async getMyStatistics(): Promise<Statistics> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/my`);
  }

  /**
   * Get company statistics (company admin)
   */
  async getCompanyStatistics(): Promise<Statistics> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/company`);
  }

  /**
   * Get platform statistics (platform admin)
   */
  async getPlatformStatistics(): Promise<Statistics> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/platform`);
  }

  /**
   * Get course-specific statistics
   */
  async getCourseStatistics(courseId: number): Promise<any> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/course/${courseId}`);
  }

  /**
   * Get dashboard data
   */
  async getDashboard(period: number = 30): Promise<any> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/dashboard?period=${period}`);
  }

  /**
   * Helper: Create xAPI statement for lesson started
   */
  createLessonStartedStatement(
    userId: number,
    userEmail: string,
    lessonId: number,
    lessonTitle: string,
    courseId?: number
  ): XApiStatement {
    return {
      actor: {
        objectType: 'Agent',
        account: {
          homePage: window.location.origin,
          name: userId.toString(),
        },
        mbox: `mailto:${userEmail}`,
      },
      verb: {
        id: 'http://adlnet.gov/expapi/verbs/launched',
        display: { 'en-US': 'launched', 'de-DE': 'gestartet' },
      },
      object: {
        id: `${window.location.origin}/lessons/${lessonId}`,
        objectType: 'Activity',
        definition: {
          name: { 'en-US': lessonTitle },
          type: 'http://adlnet.gov/expapi/activities/lesson',
        },
      },
      context: courseId
        ? {
            contextActivities: {
              parent: [
                {
                  id: `${window.location.origin}/courses/${courseId}`,
                  objectType: 'Activity',
                },
              ],
            },
          }
        : undefined,
    };
  }

  /**
   * Helper: Create xAPI statement for lesson completed
   */
  createLessonCompletedStatement(
    userId: number,
    userEmail: string,
    lessonId: number,
    lessonTitle: string,
    result?: {
      score?: number;
      success?: boolean;
      duration?: number;
    },
    courseId?: number
  ): XApiStatement {
    const statement: XApiStatement = {
      actor: {
        objectType: 'Agent',
        account: {
          homePage: window.location.origin,
          name: userId.toString(),
        },
        mbox: `mailto:${userEmail}`,
      },
      verb: {
        id: 'http://adlnet.gov/expapi/verbs/completed',
        display: { 'en-US': 'completed', 'de-DE': 'abgeschlossen' },
      },
      object: {
        id: `${window.location.origin}/lessons/${lessonId}`,
        objectType: 'Activity',
        definition: {
          name: { 'en-US': lessonTitle },
          type: 'http://adlnet.gov/expapi/activities/lesson',
        },
      },
      result: {
        completion: true,
        success: result?.success,
      },
      context: courseId
        ? {
            contextActivities: {
              parent: [
                {
                  id: `${window.location.origin}/courses/${courseId}`,
                  objectType: 'Activity',
                },
              ],
            },
          }
        : undefined,
    };

    if (result?.score !== undefined) {
      statement.result!.score = {
        scaled: result.score / 100,
        raw: result.score,
        min: 0,
        max: 100,
      };
    }

    if (result?.duration) {
      statement.result!.duration = this.formatDuration(result.duration);
    }

    return statement;
  }

  /**
   * Helper: Create xAPI statement for video watched
   */
  createVideoWatchedStatement(
    userId: number,
    userEmail: string,
    lessonId: number,
    videoTitle: string,
    progress: number,
    duration: number
  ): XApiStatement {
    return {
      actor: {
        objectType: 'Agent',
        account: {
          homePage: window.location.origin,
          name: userId.toString(),
        },
        mbox: `mailto:${userEmail}`,
      },
      verb: {
        id: 'http://adlnet.gov/expapi/verbs/progressed',
        display: { 'en-US': 'progressed', 'de-DE': 'fortgeschritten' },
      },
      object: {
        id: `${window.location.origin}/lessons/${lessonId}/video`,
        objectType: 'Activity',
        definition: {
          name: { 'en-US': videoTitle },
          type: 'https://w3id.org/xapi/video/activity-type/video',
        },
      },
      result: {
        completion: progress >= 0.9,
        extensions: {
          'https://w3id.org/xapi/video/extensions/progress': progress,
          'https://w3id.org/xapi/video/extensions/time': duration,
        },
      },
    };
  }

  /**
   * Helper: Format duration in ISO 8601 format (PT1H30M5S)
   */
  private formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    let duration = 'PT';
    if (hours > 0) duration += `${hours}H`;
    if (minutes > 0) duration += `${minutes}M`;
    if (secs > 0 || duration === 'PT') duration += `${secs}S`;

    return duration;
  }

  /**
   * Helper: Generate UUID v4
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export const trackingService = new TrackingService();
export default trackingService;
